/**
 * Checkout Redirect Helper
 *
 * Builds redirect URLs for checkout error recovery and room selection change.
 * Constructs PDP URLs with available checkout parameters.
 */

import { serializePaxString } from '@/utils/parsing/paxParser';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import type { RoomOccupancy } from '@/types/SearchBar';

export interface CheckoutRedirectParams {
  pdpUrl: string;
  checkIn?: Date | null;
  checkOut?: Date | null;
  nightCount?: number;
  roomOccupancy?: RoomOccupancy | null;
}

/**
 * Build a checkout redirect URL with available parameters
 *
 * @param params - URL parameters to include
 * @returns Full URL string or null if pdpUrl is invalid
 *
 * @example
 * ```typescript
 * const url = buildCheckoutRedirectUrl({
 *   pdpUrl: '/de/hotel/example',
 *   checkIn: new Date('2024-07-01'),
 *   checkOut: new Date('2024-07-08'),
 *   nightCount: 7,
 *   roomOccupancy: { rooms: [{ persons: [{ type: 'adult' }, { type: 'adult' }] }] }
 * });
 * // Returns: '/de/hotel/example?date=2024-07-01&todate=2024-07-08&days=7&pax=r35-35'
 * ```
 */
export function buildCheckoutRedirectUrl(params: CheckoutRedirectParams): string | null {
  if (!params.pdpUrl) {
    return null;
  }

  try {
    const url = new URL(params.pdpUrl, window.location.origin);

    if (params.checkIn) {
      url.searchParams.set('date', formatDateLocal(params.checkIn));
    }

    if (params.checkOut) {
      url.searchParams.set('todate', formatDateLocal(params.checkOut));
    }

    if (params.nightCount && params.nightCount > 0) {
      url.searchParams.set('days', params.nightCount.toString());
    }

    if (params.roomOccupancy) {
      const paxString = serializePaxString(params.roomOccupancy);
      if (paxString) {
        url.searchParams.set('pax', paxString);
      }
    }

    return url.toString();
  } catch (error) {
    console.warn('[checkoutRedirectHelper] Failed to build redirect URL:', error);
    return null;
  }
}

/**
 * Navigate to redirect URL or fall back to browser history
 *
 * @param redirectUrl - Pre-built redirect URL or null
 */
export function navigateToUrlOrBack(redirectUrl: string | null): void {
  if (redirectUrl) {
    window.location.href = redirectUrl;
  } else {
    window.history.back();
  }
}
